/**
 * stock-priority-grid.js
 *
 * Garante que a listagem de uma collection (e da página /collections/all)
 * mostre SEMPRE primeiro os produtos com estoque e, depois, os esgotados.
 *
 * Como o Shopify pagina o collection.products no servidor, a separação
 * em Liquid só vale por página. Aqui buscamos todas as páginas via AJAX,
 * juntamos, ordenamos (em estoque → esgotados) e transformamos o
 * "load more" / "infinite scroll" em um reveal de cache local.
 */
(function () {
	const APPLIED_FLAG = "__stockPriorityApplied";
	const FETCHING_FLAG = "__stockPriorityFetching";

	function isSoldOut(card) {
		if (!card || card.nodeType !== 1) return false;
		if (card.classList && card.classList.contains("esgo")) return true;
		return !!card.querySelector(".esgo");
	}

	function sortByStock(cards) {
		const inStock = [];
		const soldOut = [];
		cards.forEach((card) => {
			(isSoldOut(card) ? soldOut : inStock).push(card);
		});
		return inStock.concat(soldOut);
	}

	function getContainer() {
		return document.getElementById("ProductGridContainer");
	}

	function getGrid(container) {
		return container && container.querySelector(".load-more-grid");
	}

	async function applyStockPriority() {
		const container = getContainer();
		if (!container) return;
		if (container.dataset.sortOrder === 'manual') return;  // ← nova linha
		if (container[APPLIED_FLAG] || container[FETCHING_FLAG]) return;

		const grid = getGrid(container);
		if (!grid) return;

		container[FETCHING_FLAG] = true;

		const totalPagesInput = container.querySelector("[data-total-pages]");
		const totalPages = totalPagesInput
			? parseInt(totalPagesInput.value, 10) || 1
			: 1;
		const perPage =
			parseInt(container.dataset.perPage, 10) ||
			grid.children.length ||
			12;

		// Desabilita botão de load-more enquanto pré-carregamos
		const initialBtn = container.querySelector(".js-load-more");
		if (initialBtn) initialBtn.setAttribute("disabled", "");

		let allCards = Array.from(grid.children);

		if (totalPages > 1) {
			const nextUrlInput = container.querySelector("[data-next-url]");
			const baseUrl = nextUrlInput ? nextUrlInput.value : null;
			if (baseUrl) {
				const fetches = [];
				for (let p = 2; p <= totalPages; p++) {
					const url = baseUrl.replace(/page=\d+/, "page=" + p);
					fetches.push(
						fetch(url)
							.then((r) => r.text())
							.then((html) => {
								const doc = new DOMParser().parseFromString(
									html,
									"text/html"
								);
								const otherGrid = doc.querySelector(
									".load-more-grid"
								);
								return otherGrid
									? Array.from(otherGrid.children)
									: [];
							})
							.catch(() => [])
					);
				}
				const results = await Promise.all(fetches);
				results.forEach((arr) => {
					allCards = allCards.concat(arr);
				});
			}
		}

		const sorted = sortByStock(allCards);

		container.__sortedCards = sorted;
		container.__perPage = perPage;

		// Re-renderiza apenas os primeiros perPage no grid
		grid.innerHTML = "";
		sorted.slice(0, perPage).forEach((c) => grid.appendChild(c));
		recomputeRowClasses(grid);

		// Atualiza inputs de paginação para refletir o cache
		const newTotalPages = Math.max(1, Math.ceil(sorted.length / perPage));
		container
			.querySelectorAll("[data-current-page]")
			.forEach((i) => (i.value = 1));
		container
			.querySelectorAll("[data-total-pages]")
			.forEach((i) => (i.value = newTotalPages));

		bindRevealHandlers(container);

		if (sorted.length <= perPage) {
			const btn = container.querySelector(".js-load-more");
			if (btn) btn.remove();
			const inf = container.querySelector(".js-infinite-scroll");
			if (inf) inf.remove();
		} else if (initialBtn) {
			initialBtn.removeAttribute("disabled");
		}

		container[APPLIED_FLAG] = true;
		container[FETCHING_FLAG] = false;

		if (typeof window.colorSwatches === "function") {
			try {
				window.colorSwatches();
			} catch (_) {}
		}
		if (typeof window.updateShowingText === "function") {
			try {
				window.updateShowingText();
			} catch (_) {}
		}
	}

	function recomputeRowClasses(grid) {
		const perRowVar = getComputedStyle(grid)
			.getPropertyValue("--per-row")
			.trim();
		const perRow = parseInt(perRowVar, 10) || 0;
		if (!perRow) return;
		Array.from(grid.children).forEach((card, idx) => {
			card.classList.remove("hide_border");
			if (((idx + 1) % perRow) === 0) {
				card.classList.add("hide_border");
			}
		});
	}

	function revealNext(container) {
		const grid = getGrid(container);
		const cached = container.__sortedCards;
		const perPage = container.__perPage || 12;
		if (!grid || !cached) return;

		const shown = grid.children.length;
		const next = cached.slice(shown, shown + perPage);
		if (!next.length) return;
		next.forEach((c) => grid.appendChild(c));
		recomputeRowClasses(grid);

		container.querySelectorAll("[data-current-page]").forEach((i) => {
			i.value = (parseInt(i.value, 10) || 1) + 1;
		});

		if (grid.children.length >= cached.length) {
			const btn = container.querySelector(".js-load-more");
			if (btn) btn.remove();
			const inf = container.querySelector(".js-infinite-scroll");
			if (inf) inf.remove();
		}

		if (typeof window.colorSwatches === "function") {
			try {
				window.colorSwatches();
			} catch (_) {}
		}
		if (typeof window.updateShowingText === "function") {
			try {
				window.updateShowingText();
			} catch (_) {}
		}
	}

	function bindRevealHandlers(container) {
		const btn = container.querySelector(".js-load-more");
		if (btn) {
			btn.onclick = (e) => {
				e.preventDefault();
				revealNext(container);
			};
		}

		// Substitui o observer original por um nosso, que revela do cache
		const infList = container.querySelectorAll(".js-infinite-scroll");
		infList.forEach((inf) => {
			const clone = inf.cloneNode(true);
			inf.parentNode.replaceChild(clone, inf);
			const observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						revealNext(container);
						if (!document.body.contains(clone)) {
							observer.disconnect();
						}
					}
				});
			});
			observer.observe(clone);
		});
	}

	function init() {
		applyStockPriority();
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}

	// Re-aplica depois que facets.js troca o conteúdo do grid via AJAX
	document.addEventListener("stock-priority:reapply", () => {
		const container = getContainer();
		if (container) {
			container[APPLIED_FLAG] = false;
			container[FETCHING_FLAG] = false;
			container.__sortedCards = null;
		}
		applyStockPriority();
	});

	window.applyStockPriority = applyStockPriority;
})();
